import type { FilteredProduct, RankDecoration } from '@/api';
import type { MedicationPadItemType, ReissueStatusType } from '@/components/Patient/components/PrescriptionPad';
import type { MedicationType } from '@/components/Patient/components/ProductDetailsCard/ProductDetailsCard.types';
import { transformFilteredProductToPrescriptionMetadata } from '@/components/Patient/components/ProductDetailsCard/ProductDetailsCard.utils';
import type { ProductFormulation } from '@/constants/products';
import { DEFAULT_QUESTION_ID, REISSUED_ANSWER_ID } from '@/shared-ui/TierLabel/TierLabel.types';
import { getDosagePrefill } from '@/utils/dosageLimit/getDosagePrefill';
import { getFormulationFilter } from '@/utils/dosageLimit/getFormulationFilter';
import { RankTier } from '@montugroup/pms-api-contracts';
import type { PrescriptionHistoryStatePreviousItem, PrescriptionPadRules } from '../types';

/**
 * Creates a new reissued medication by merging it with existing prescribed medications,
 * considering out-of-stock or inactive scenarios.
 *
 * @param availableProducts - All available medications for the doctor to choose from.
 * @param reissuedMedicationItem - The reissued medication to be added to the pad.
 * @param previouslyPrescribedItem - Previously prescribed item from prescription history.
 * @param rules - Pad rules from the feature flags: whether to split the maximum daily dose out of the dosage text.
 * @returns A new reissued medication item or undefined if productId is missing or invalid. Its alert is left to the
 * caller, which validates it with the pad it joins.
 */
export const createReissuedMedication = (
  availableProducts: FilteredProduct[],
  reissuedMedicationItem: MedicationPadItemType,
  previouslyPrescribedItem: PrescriptionHistoryStatePreviousItem | undefined,
  rules: PrescriptionPadRules,
): MedicationPadItemType | undefined => {
  const { medicationPadItem } = reissuedMedicationItem;
  const productId = medicationPadItem?.productId;

  // Validate and parse the productId to a number
  const numericProductId = productId !== undefined && !isNaN(Number(productId)) ? Number(productId) : undefined;
  if (numericProductId === undefined) {
    return undefined;
  }

  const matchingProduct = availableProducts.find((product) => product.id === numericProductId);

  // Ensure medicationPadItem is defined before calling getUpdatedMedicationPadItem
  const updatedMedicationPadItem =
    matchingProduct && medicationPadItem
      ? getUpdatedMedicationPadItem(
          medicationPadItem,
          matchingProduct,
          previouslyPrescribedItem?.prescriptionDetails,
          getFormulationFilter(reissuedMedicationItem.filters),
          rules,
        )
      : medicationPadItem;

  const isOutsideTopRecommendation = matchingProduct ? !isTopRecommendation(matchingProduct) : false;

  const updatedReissueStatus = buildReissueStatus(
    reissuedMedicationItem.reissueStatus,
    medicationPadItem,
    matchingProduct,
  );

  // Build the new MedicationPadItemType object
  return {
    ...reissuedMedicationItem,
    medicationPadItem: updatedMedicationPadItem,
    moreProductsLoaded: false,
    feedback: isOutsideTopRecommendation
      ? {
          questionAsked: DEFAULT_QUESTION_ID,
          answerSelected: REISSUED_ANSWER_ID,
        }
      : undefined,
    reissueStatus: updatedReissueStatus,
  };
};

const getUpdatedMedicationPadItem = (
  medicationPadItem: MedicationType,
  matchingProduct: FilteredProduct,
  previousPrescriptionDetails: PrescriptionHistoryStatePreviousItem['prescriptionDetails'] | undefined,
  formulation: ProductFormulation | undefined,
  rules: PrescriptionPadRules,
): MedicationType => {
  const updatedMedicationPadItem: MedicationType = {
    ...medicationPadItem,
    ...(previousPrescriptionDetails
      ? {
          dosage: previousPrescriptionDetails.dosage,
          interval: previousPrescriptionDetails.interval,
          repeats: previousPrescriptionDetails.repeats,
          quantity: previousPrescriptionDetails.quantity,
          productName: matchingProduct.productName,
          supplierName: matchingProduct.supplierName,
          dispenseSize: matchingProduct.attributes?.dispenseSize,
        }
      : transformFilteredProductToPrescriptionMetadata(matchingProduct)),
  };
  return {
    ...updatedMedicationPadItem,
    ...getDosagePrefill(updatedMedicationPadItem.dosage, formulation, rules, {
      source: previousPrescriptionDetails ? 'reissue' : 'product',
      productId: matchingProduct.id,
    }),
  };
};

const isTopRecommendation = (product: FilteredProduct): boolean => {
  const decoration = product.decoration as RankDecoration;
  return decoration.tier === RankTier.First;
};

const buildReissueStatus = (
  currentReissueStatus: ReissueStatusType | undefined,
  medicationPadItem: MedicationType | undefined,
  matchingProduct?: FilteredProduct,
): ReissueStatusType => {
  const fallbackProductId =
    medicationPadItem?.productId !== undefined && !isNaN(Number(medicationPadItem.productId))
      ? Number(medicationPadItem.productId)
      : undefined;

  const productId = matchingProduct?.id ?? fallbackProductId;
  const productName = matchingProduct?.productName || medicationPadItem?.productName || '';
  const supplierName = matchingProduct?.supplierName || medicationPadItem?.supplierName || '';
  const description = matchingProduct?.description || medicationPadItem?.description || '';

  return {
    ...currentReissueStatus,
    productId,
    productName,
    supplierName,
    description,
  };
};
